import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Box,
} from '@mui/material';
import LoadingButton from './LoadingButton';
import { receivedEmailsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatMoney } from '../utils/money';

// "HDFC Bank Cards <statements@hdfcbank.net>" -> "HDFC Bank Cards"
const senderName = (from) => String(from || '').replace(/<.*>/, '').replace(/"/g, '').trim();

/**
 * Open a statement's password-protected PDF and read it again: the bill gets
 * its total, minimum and due date. With "remember", later statements from the
 * same sender open on their own (and earlier locked ones are opened now).
 */
const UnlockPdfDialog = ({ open, emailId, onClose, onUnlocked }) => {
  const { showSuccess } = useNotification();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !emailId) return;
    setPassword('');
    setError('');
    setEmail(null);
    setLoading(true);
    receivedEmailsApi.getById(emailId)
      .then(response => {
        setEmail(response.data);
        setLabel(senderName(response.data.fromAddress));
      })
      .catch(() => setError('Could not load the email'))
      .finally(() => setLoading(false));
  }, [open, emailId]);

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await receivedEmailsApi.unlockPdf(emailId, { password, remember, label });
      const bill = response.data.bill;
      const figures = bill && bill.totalDue !== null
        ? `: ${formatMoney(bill.totalDue)} due${bill.dueDate ? ` on ${new Date(bill.dueDate).toLocaleDateString()}` : ''}`
        : '';
      showSuccess(`Statement read${figures}${remember ? '. Other statements from this sender are being opened.' : ''}`);
      if (onUnlocked) onUnlocked(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'The statement could not be read');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Open statement PDF</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {email && (
              <Box>
                <Typography variant="subtitle2">{email.subject}</Typography>
                <Typography variant="body2" color="text.secondary">{senderName(email.fromAddress)}</Typography>
              </Box>
            )}
            {email && !email.pdf && (
              <Alert severity="warning">This email has no PDF attached.</Alert>
            )}
            {email?.pdf?.hint && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>What the bank says about the password</Typography>
                <Typography variant="body2">{email.pdf.hint}</Typography>
              </Alert>
            )}
            <TextField
              label="PDF password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
              autoFocus
              fullWidth
              onKeyDown={(e) => { if (e.key === 'Enter' && password) handleSubmit(); }}
            />
            <FormControlLabel
              control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
              label="Remember it for statements from this sender"
            />
            {remember && (
              <TextField
                label="Name"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                helperText="Shown in Settings > Email > Statement passwords"
                size="small"
                fullWidth
              />
            )}
            <Typography variant="body2" color="text.secondary">
              The password is checked here before anything is read. Saved passwords are stored encrypted and never shown again.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton variant="contained" onClick={handleSubmit} loading={saving} disabled={!password || loading}>
          Open and read
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UnlockPdfDialog;
